import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import CreateDealUpdate from './CreateDealUpdate';
import { apiGetById } from '../../services/genesisApi/httpsMethods';
import { Alert, CircularProgress, Box } from '@mui/material';
import type { PartialDeal } from '../../types/deal';

type LoadResult = {
  /** Route id this result belongs to; a different id means still loading. */
  id: string | undefined;
  deal: PartialDeal | null;
  error: string | null;
};

const EditDealWrapper = () => {
  const { dealId } = useParams();
  const [result, setResult] = useState<LoadResult | null>(null);
  // Never show the previous deal (or a blank Create form) while a new id loads
  const loading = !result || result.id !== dealId;

  useEffect(() => {
    let cancelled = false;

    const fetchDeal = async () => {
      try {
        const response = await apiGetById('deal', Number(dealId)); // API call
        if (cancelled) return;
        if (response && response.success && response.data?.[0]) {
          setResult({ id: dealId, deal: response.data[0], error: null });
        } else {
          setResult({
            id: dealId,
            deal: null,
            error:
              !response?.success && response?.status !== 404
                ? 'Failed to load the deal. Please try again.'
                : 'Deal not found.',
          });
        }
      } catch (error) {
        console.error('Failed to fetch deal', error);
        if (!cancelled) {
          setResult({
            id: dealId,
            deal: null,
            error: 'Failed to load the deal. Please try again.',
          });
        }
      }
    };

    fetchDeal();
    return () => {
      cancelled = true;
    };
  }, [dealId]);

  if (loading) {
    return (
      <Box sx={{ p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Without a loaded deal the form would act as Create
  if (result.error || !result.deal) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{result.error || 'Deal not found.'}</Alert>
      </Box>
    );
  }

  return (
    <CreateDealUpdate key={dealId} deal={result.deal} deleteOption={true} />
  );
};

export default EditDealWrapper;
